import { useEffect, useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { getResizeDirection, getResizeStep } from '../core';
import type { NodeChange, ResizeControlPosition, ResizeLimits, ResizeValues } from '../core';
import { useStoreApi } from '../store';
import type { MindmapStoreApi } from '../store';
import { suppressNextClick } from '../utils';

const PRIMARY_BUTTON = 0;

export type ResizeParams = ResizeValues;
export type ResizeParamsWithDirection = ResizeValues & { direction: [number, number] };

export type ResizeCallbacks = {
  onResizeStart?: (event: PointerEvent, params: ResizeParams) => void;
  onResize?: (event: PointerEvent, params: ResizeParamsWithDirection) => void;
  onResizeEnd?: (event: PointerEvent, params: ResizeParams) => void;
};

/** `callbacks` are the ones in place when the gesture started (d3-drag behaviour the app relies on — see below). */
type Session = {
  pointerId: number;
  clientX: number;
  clientY: number;
  start: ResizeValues;
  previous: ResizeValues;
  hasMoved: boolean;
  callbacks: ResizeCallbacks;
};

const startValues = (store: MindmapStoreApi, nodeId: string): ResizeValues | null => {
  const internal = store.getState().nodeLookup.get(nodeId);
  if (!internal) return null;
  const { position } = internal.node;
  return { width: internal.width ?? 0, height: internal.height ?? 0, x: position.x, y: position.y };
};

/** v11 changes for one resize step: a `position` change first (top/left controls), then the size with `updateStyle`. */
const stepChanges = (nodeId: string, values: ResizeValues, isPositionChange: boolean, isSizeChange: boolean): NodeChange[] => [
  ...(isPositionChange ? [{ id: nodeId, type: 'position' as const, position: { x: values.x, y: values.y } }] : []),
  ...(isSizeChange
    ? [{ id: nodeId, type: 'dimensions' as const, updateStyle: true, resizing: true, dimensions: { width: values.width, height: values.height } }]
    : []),
];

/** Drags one resize control of a node and reports v11 resize callbacks and changes. */
export const useResizeControl = (
  controlRef: RefObject<HTMLElement>,
  nodeId: string | null,
  control: ResizeControlPosition,
  limits: ResizeLimits,
  callbacks: ResizeCallbacks,
): void => {
  const store = useStoreApi();
  const callbacksRef = useRef(callbacks);
  useLayoutEffect(() => {
    callbacksRef.current = callbacks;
  });
  const { minWidth, minHeight, maxWidth, maxHeight } = limits;

  useEffect(() => {
    const element = controlRef.current;
    if (!element || !nodeId) return undefined;
    const stepLimits = { minWidth, minHeight, maxWidth, maxHeight };
    let session: Session | null = null;

    const handlePointerDown = (event: PointerEvent) => {
      const start = startValues(store, nodeId);
      if (event.button !== PRIMARY_BUTTON || !start) return;
      // v11 (d3-drag) keeps the callbacks from the start of the gesture. The app's onResizeEnd therefore sees its
      // pre-resize `nodes` and writes the old `style`/`width` back — snapshot saves depend on that (payload parity).
      const callbacks = callbacksRef.current;
      session = { pointerId: event.pointerId, clientX: event.clientX, clientY: event.clientY, start, previous: start, hasMoved: false, callbacks };
      element.setPointerCapture(event.pointerId);
      callbacks.onResizeStart?.(event, { ...start });
    };
    const handlePointerMove = (event: PointerEvent) => {
      if (session?.pointerId !== event.pointerId) return;
      const { zoom } = store.getState().viewport;
      const delta = { x: (event.clientX - session.clientX) / zoom, y: (event.clientY - session.clientY) / zoom };
      const { values, isSizeChange, isPositionChange } = getResizeStep(control, session.start, session.previous, delta, stepLimits);
      if (!isSizeChange && !isPositionChange) return;
      const direction = getResizeDirection(control, session.previous, values);
      session.previous = values;
      session.hasMoved = true;
      session.callbacks.onResize?.(event, { ...values, direction });
      store.getState().triggerNodeChanges(stepChanges(nodeId, values, isPositionChange, isSizeChange));
    };
    const handlePointerEnd = (event: PointerEvent) => {
      if (session?.pointerId !== event.pointerId) return;
      const { previous, hasMoved, callbacks } = session;
      session = null;
      if (element.hasPointerCapture(event.pointerId)) element.releasePointerCapture(event.pointerId);
      callbacks.onResizeEnd?.(event, { ...previous });
      store.getState().triggerNodeChanges([{ id: nodeId, type: 'dimensions', resizing: false }]);
      if (hasMoved && element.ownerDocument.defaultView) suppressNextClick(element.ownerDocument.defaultView);
    };

    element.addEventListener('pointerdown', handlePointerDown);
    element.addEventListener('pointermove', handlePointerMove);
    element.addEventListener('pointerup', handlePointerEnd);
    element.addEventListener('pointercancel', handlePointerEnd);
    return () => {
      element.removeEventListener('pointerdown', handlePointerDown);
      element.removeEventListener('pointermove', handlePointerMove);
      element.removeEventListener('pointerup', handlePointerEnd);
      element.removeEventListener('pointercancel', handlePointerEnd);
    };
  }, [controlRef, nodeId, control, minWidth, minHeight, maxWidth, maxHeight, store]);
};
